"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { Bell, BellOff, Loader2, Search, UserPlus, Users, X } from "lucide-react"

import { Button } from "./ui/button"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"
import { MessageAvatar } from "./ui/message-avatar"
import { useConversationParticipants } from "../hooks/use-conversation-participants"
import { cn } from "../lib/utils"
import type { MessageApiService } from "../services/api-service"
import type { ConversationParticipant, ConversationParticipantCandidate } from "../types"

/**
 * Who is on this chat, and adding or removing them.
 *
 * **Everything here is about notifications, not access.** Removing somebody stops their signals and
 * leaves their ability to open the thread from the record untouched — the copy says so explicitly,
 * because otherwise people hesitate to remove a colleague in case it cuts them off from the record.
 *
 * The two profile types are not symmetric and the UI has to show that rather than hide it. Internal
 * users can be searched and added freely. Client users appear only when they already hold access to
 * the record and came off the chat, so they can be re-added but never newly invited — which is why the
 * search box is internal-only and the empty state differs by caller.
 */
export function EntityChatParticipants({
  api,
  entityTypeId,
  entityId,
  entityLabel,
  currentProfileId,
  canSearchDirectory,
  canManage = true,
  roleLabels,
  t,
  onClientAccessChange
}: {
  api: MessageApiService | null
  entityTypeId: string
  entityId: string
  /**
   * What to call the record in the pill — "Assignment participants", not "WO participants". Passed in
   * rather than derived here because the panel already owns entity-type naming, and two copies of that
   * mapping is how one of them ends up saying Work Order for an assignment.
   */
  entityLabel?: string
  currentProfileId?: number | null
  /** Internal callers only. Client users get the re-add list without a search box. */
  canSearchDirectory: boolean
  /** False when several entities are selected: adding acts on one thread and there is no single one. */
  canManage?: boolean
  /**
   * Contextual-role code → display label, from the host's code-value source. When absent, roles fall
   * back to an i18n key and then the raw code (see {@link ParticipantRow}).
   */
  roleLabels?: Record<string, string>
  t: (key: string, options?: Record<string, unknown>) => string
  /**
   * Reports whether a CLIENT can see this thread yet — true once any non-internal (organizational)
   * participant is present, i.e. gw-sync has pushed client access. The panel uses it to open or grey the
   * Client message lane, matching the backend's "wait for the client" send gate.
   */
  onClientAccessChange?: (hasClientAccess: boolean) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [term, setTerm] = useState("")

  const {
    participants,
    subscribedCount,
    participantCount,
    candidates,
    isLoading,
    isSearching,
    pendingProfileIds,
    error,
    search,
    setParticipant
    // Loaded on mount, not on open: the pill shows the count, and a count that only appears after you
    // click it is not a count, it is a reward for guessing.
  } = useConversationParticipants({ api, entityTypeId, entityId, enabled: true })

  // Tell the panel whether a CLIENT participant is on this thread — an ORGANIZATIONAL profile
  // specifically, not merely non-internal. A Worker is also non-internal but is not a client, so keying
  // off `!isInternal` would wrongly open the client lane on a worker-only thread. Reported from the same
  // participant list the pill already shows rather than a second call.
  useEffect(() => {
    onClientAccessChange?.(participants.some(participant => participant.profileType === "Organizational"))
  }, [participants, onClientAccessChange])

  // Debounced so typing a name is one request at the end rather than one per keystroke.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!isAdding) return

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void search(term), 250)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [isAdding, term, search])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setIsAdding(false)
    setTerm("")
  }, [])

  const pillLabel = entityLabel
    ? t("participants.entityTitle", { entity: entityLabel })
    : t("participants.title")

  return (
    <Popover open={isOpen} onOpenChange={open => (open ? setIsOpen(true) : handleClose())}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            {/* The anchor as well as a trigger. "Add" below opens the same popover, and Radix
                positions against a trigger — so with both registered the list hung off "Add" and
                appeared adrift to the right of the button it describes. Anchoring the button itself
                pins the list under it whichever control opened it. */}
            <PopoverAnchor asChild>
              <Button
                variant="ghost"
                className="entity-chat-panel-participants-trigger"
                aria-label={pillLabel}
              >
                <Users size={14} />
                <span className="entity-chat-panel-participants-trigger-label">{pillLabel}</span>
                {participantCount > 0 && (
                  <span className="entity-chat-panel-participants-count">{participantCount}</span>
                )}
              </Button>
            </PopoverAnchor>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{pillLabel}</p>
        </TooltipContent>
      </Tooltip>

      {/* Add sits OUTSIDE the popover trigger and opens it straight into the picker. Adding somebody
          is the action people come here for; making them open a list first and find a second button
          is a step that exists only because of how the component happens to be built. */}
      {canManage && canSearchDirectory && (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="entity-chat-panel-participants-add-inline"
                onClick={() => setIsAdding(true)}
              >
                <UserPlus size={14} />
                <span>{t("participants.addShort")}</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("participants.addBarTooltip")}</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Directly under the button, nudged right rather than flush left: the panel's resize handle
          runs down that edge and is a 6px target you cannot hit while a popover covers it. */}
      <PopoverContent
        className="entity-chat-panel-participants-popover"
        align="start"
        side="bottom"
        sideOffset={8}
        alignOffset={4}
        collisionPadding={12}
      >
        <div className="entity-chat-panel-participants-header">
          <div>
            <h4 className="entity-chat-panel-participants-title">{t("participants.title")}</h4>
            <p className="entity-chat-panel-participants-subtitle">
              {participantCount === subscribedCount
                ? t("participants.countLabel", { count: participantCount })
                : t("participants.countLabelWithMuted", {
                    count: participantCount,
                    muted: participantCount - subscribedCount
                  })}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} aria-label={t("participants.title")}>
            <X size={14} />
          </Button>
        </div>

        {error && <div className="entity-chat-panel-participants-error">{t(error)}</div>}

        {isAdding ? (
          <AddPeople
            canSearchDirectory={canSearchDirectory}
            term={term}
            onTermChange={setTerm}
            candidates={candidates}
            isSearching={isSearching}
            pendingProfileIds={pendingProfileIds}
            onAdd={profileId => void setParticipant(profileId, true)}
            onDone={() => {
              setIsAdding(false)
              setTerm("")
            }}
            t={t}
          />
        ) : (
          <>
            <div className="entity-chat-panel-participants-list">
              {isLoading && (
                <div className="entity-chat-panel-participants-status">
                  <Loader2 className="entity-chat-panel-spin" size={14} />
                  <span>{t("participants.loading")}</span>
                </div>
              )}

              {!isLoading && participants.length === 0 && (
                <p className="entity-chat-panel-participants-empty">{t("participants.empty")}</p>
              )}

              {participants.map(participant => (
                <ParticipantRow
                  key={participant.profileId}
                  participant={participant}
                  isSelf={participant.profileId === currentProfileId}
                  canManageOthers={canSearchDirectory}
                  isPending={pendingProfileIds.has(participant.profileId)}
                  onToggle={subscribed => void setParticipant(participant.profileId, subscribed)}
                  roleLabels={roleLabels}
                  t={t}
                />
              ))}
            </div>

            <div className="entity-chat-panel-participants-footer">
              <Button
                variant="outline"
                className="entity-chat-panel-participants-add-button entity-chat-panel-participants-add-primary"
                onClick={() => setIsAdding(true)}
              >
                <UserPlus size={14} />
                <span>{t("participants.addPeople")}</span>
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Date and time of a participation change, in the viewer's locale. Both halves on purpose — "added
 * today" is the common case and a bare date would render it as useless.
 */
function formatParticipationDate(value: string): string {
  const when = new Date(value)
  if (Number.isNaN(when.getTime())) return ""
  return when.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  })
}

function ParticipantRow({
  participant,
  isSelf,
  canManageOthers,
  isPending,
  onToggle,
  roleLabels,
  t
}: {
  participant: ConversationParticipant
  isSelf: boolean
  /** Internal callers only. A client may mute themselves and nobody else. */
  canManageOthers: boolean
  isPending: boolean
  onToggle: (subscribed: boolean) => void
  roleLabels?: Record<string, string>
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  /**
   * Why this person is on the thread.
   *
   * A contextual participant is described by their ROLE — "Assigned to", "Recruiter" — because that is
   * the actual reason they are here, and it is a property of the assignment rather than of them.
   * Somebody added by hand has no role at all, so they read as an invitee, with the profile type in
   * brackets to keep the internal/client distinction visible.
   *
   * A code resolves in order: the host-supplied `roleLabels` map (the code-value descriptions), then an
   * i18n key, then the raw code — so a code with no mapping still renders as itself rather than as
   * "participants.roles.FOO".
   */
  const profileTypeLabel = participant.isInternal
    ? t("participants.internal")
    : t("participants.client")

  const resolveRoleLabel = (code: string): string =>
    roleLabels?.[code] ?? t(`participants.roles.${code}`, { defaultValue: code })

  const roleCodes = participant.roleCodes ?? []
  const resolvedRoles = roleCodes.map(resolveRoleLabel)
  // Collapse multiple roles to "FirstRole +N" so the chip stays short — the full list is on the chip's
  // hover title. A comma-joined list stretched the chip and squeezed the name column.
  const roleLabel =
    participant.source === "granted"
      ? `${t("participants.invitee")} (${profileTypeLabel})`
      : resolvedRoles.length > 1
        ? `${resolvedRoles[0]} +${resolvedRoles.length - 1}`
        : resolvedRoles.length === 1
          ? resolvedRoles[0]
          : profileTypeLabel

  // When roles are collapsed, the full list is the more useful hover than the participation date.
  const roleTitle = resolvedRoles.length > 1 ? resolvedRoles.join(", ") : undefined

  // Email is shown to internal viewers only; the identity also carries a hover title so an internal
  // viewer can read a name/email that truncates in the row. (`canManageOthers` is the viewer-is-internal
  // flag — only internal callers can search the directory and manage others.)
  const viewerIsInternal = canManageOthers
  const identityTitle = viewerIsInternal && participant.email
    ? `${participant.name} — ${participant.email}`
    : participant.name

  /**
   * When somebody was put on the thread, for the pill's hover.
   *
   * Only present for a participant who was explicitly added, removed, re-added or who left — a
   * contextual participant has no event, because nobody did anything to them: they are here through
   * their role. So the invitee pill gets a date and the role pills do not, which is the honest
   * distinction rather than an inconsistency.
   */
  const participationTitle = participant.participationChangedAt
    ? t(`participants.participation.${participant.participationAction || "added"}`, {
        defaultValue: t("participants.participation.added"),
        at: formatParticipationDate(participant.participationChangedAt)
      })
    : undefined

  const actionLabel = participant.subscribed
    ? isSelf
      ? t("participants.removeSelf")
      : t("participants.remove")
    : t("participants.add")

  const actionTooltip = participant.subscribed
    ? isSelf
      ? t("participants.removeSelfTooltip")
      : t("participants.removeTooltip")
    : t("participants.addTooltip")

  return (
    <div className={cn("entity-chat-panel-participant", !participant.subscribed && "entity-chat-panel-participant-muted")}>
      <MessageAvatar name={participant.name} size={28} className="entity-chat-panel-participant-avatar" />

      <span className="entity-chat-panel-participant-identity" title={identityTitle}>
        <span className="entity-chat-panel-participant-name-row">
          {/* The name truncates and "(You)" does not — the marker is short, fixed, and the one part
              of the row you cannot reconstruct from context, so it must never be what gets cut. */}
          <span className="entity-chat-panel-participant-name">{participant.name}</span>
          {isSelf && (
            <span className="entity-chat-panel-participant-self">({t("participants.you")})</span>
          )}
        </span>
        {viewerIsInternal && participant.email && (
          <span className="entity-chat-panel-participant-email">{participant.email}</span>
        )}
      </span>

      <span
        className={cn(
          "entity-chat-panel-participant-chip",
          (roleTitle || participationTitle) && "entity-chat-panel-participant-chip-dated",
          participant.isInternal
            ? "entity-chat-panel-participant-chip-internal"
            : "entity-chat-panel-participant-chip-client"
        )}
        // A native title rather than a Tooltip component: the popover already closes on outside
        // pointer events, and a floating tooltip inside it fought that. Nothing here needs to be
        // clickable, so the browser's own affordance is the right size of solution. The collapsed
        // role list wins over the participation date — it is the part that was truncated.
        title={roleTitle ?? participationTitle}
      >
        {roleLabel}
      </span>

      {participant.canRemove && (isSelf || canManageOthers) && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="entity-chat-panel-participant-action"
              disabled={isPending}
              onClick={() => onToggle(!participant.subscribed)}
              aria-label={actionLabel}
            >
              {/* A bell toggle, not red text: subscribed = notifications on (Bell), muted = off (BellOff).
                  Colouring mute red read as destructive/distracting when it only stops signals, never access. */}
              {isPending
                ? <Loader2 className="entity-chat-panel-spin" size={12} />
                : participant.subscribed
                  ? <Bell size={16} />
                  : <BellOff size={16} />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{actionTooltip}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

function AddPeople({
  canSearchDirectory,
  term,
  onTermChange,
  candidates,
  isSearching,
  pendingProfileIds,
  onAdd,
  onDone,
  t
}: {
  canSearchDirectory: boolean
  term: string
  onTermChange: (value: string) => void
  candidates: ConversationParticipantCandidate[]
  isSearching: boolean
  pendingProfileIds: Set<number>
  onAdd: (profileId: number) => void
  onDone: () => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  return (
    <>
      {/* No search box for client users. Their candidates are read off the record rather than
          searched, so a box that ignores what you type would be worse than none. */}
      {canSearchDirectory && (
        <div className="entity-chat-panel-participants-search">
          <Search size={14} />
          <input
            type="text"
            value={term}
            autoFocus
            onChange={event => onTermChange(event.target.value)}
            placeholder={t("participants.searchPlaceholder")}
            aria-label={t("participants.searchPlaceholder")}
          />
        </div>
      )}

      <div className="entity-chat-panel-participants-list">
        {isSearching && (
          <div className="entity-chat-panel-participants-status">
            <Loader2 className="entity-chat-panel-spin" size={14} />
            <span>{t("participants.searching")}</span>
          </div>
        )}

        {!isSearching && candidates.length === 0 && (
          <p className="entity-chat-panel-participants-empty">
            {canSearchDirectory ? t("participants.noCandidates") : t("participants.noCandidatesForClient")}
          </p>
        )}

        {candidates.map(candidate => (
          <div key={candidate.profileId} className="entity-chat-panel-participant">
            <MessageAvatar name={candidate.name} size={28} className="entity-chat-panel-participant-avatar" />

            <span className="entity-chat-panel-participant-identity">
              <span className="entity-chat-panel-participant-name">{candidate.name}</span>
              {canSearchDirectory && candidate.email && (
                <span className="entity-chat-panel-participant-email">{candidate.email}</span>
              )}
              {candidate.previouslyLeft && (
                <span className="entity-chat-panel-participant-note">{t("participants.previouslyLeft")}</span>
              )}
            </span>

            <Button
              variant="ghost"
              className="entity-chat-panel-participant-action"
              disabled={pendingProfileIds.has(candidate.profileId)}
              onClick={() => onAdd(candidate.profileId)}
            >
              {pendingProfileIds.has(candidate.profileId)
                ? <Loader2 className="entity-chat-panel-spin" size={12} />
                : t("participants.addShort")}
            </Button>
          </div>
        ))}
      </div>

      <div className="entity-chat-panel-participants-footer">
        <Button variant="outline" onClick={onDone} className="entity-chat-panel-participants-add-button entity-chat-panel-participants-add-primary">
          {t("participants.title")}
        </Button>
      </div>
    </>
  )
}
